import React from 'react';

interface StatusBadgeProps {
  status: 'online' | 'offline' | 'degraded' | 'error' | 'connecting' | 'connected' | 'disconnected' | 'unknown' | 'active' | 'inactive' | 'pending' | 'executing' | 'completed' | 'failed' | 'timeout';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'md',
  showIcon = true 
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'active':
      case 'completed':
        return {
          bg: 'bg-status-success-bg',
          text: 'text-status-success',
          border: 'border-status-success/30',
          dot: 'bg-status-success',
          label: status.charAt(0).toUpperCase() + status.slice(1),
          animate: true
        };

      case 'offline':
      case 'disconnected':
      case 'inactive':
      case 'failed':
        return {
          bg: 'bg-status-error-bg',
          text: 'text-status-error',
          border: 'border-status-error/30',
          dot: 'bg-status-error',
          label: status.charAt(0).toUpperCase() + status.slice(1),
          animate: false
        };

      case 'connecting':
      case 'pending':
      case 'executing':
        return {
          bg: 'bg-status-pending-bg',
          text: 'text-status-pending',
          border: 'border-status-pending/30',
          dot: 'bg-status-pending',
          label: status.charAt(0).toUpperCase() + status.slice(1),
          animate: true
        };

      case 'error':
        return {
          bg: 'bg-status-error-bg',
          text: 'text-status-error',
          border: 'border-status-error/30',
          dot: 'bg-status-error',
          label: 'Error',
          animate: false
        };

      case 'timeout':
        return {
          bg: 'bg-status-warning-bg',
          text: 'text-status-warning',
          border: 'border-status-warning/30',
          dot: 'bg-status-warning',
          label: 'Timeout',
          animate: false
        };

      case 'degraded':
        return {
          bg: 'bg-status-warning-bg',
          text: 'text-status-warning',
          border: 'border-status-warning/30',
          dot: 'bg-status-warning',
          label: 'Degraded',
          animate: true
        };

      case 'unknown':
      default:
        return {
          bg: 'bg-status-offline-bg',
          text: 'text-status-offline',
          border: 'border-border',
          dot: 'bg-status-offline',
          label: 'Unknown',
          animate: false
        };
    }
  };

  const config = getStatusConfig();

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const dotSizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5'
  };

  return (
    <span 
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${config.bg} ${config.text} ${config.border} ${sizeClasses[size]}
      `}
    >
      {showIcon && (
        <span className={`${dotSizeClasses[size]} ${config.dot} rounded-full ${config.animate ? 'animate-pulse' : ''}`} />
      )}
      <span>{config.label}</span>
    </span>
  );
};

